import React, { FC, useState } from 'react'
import { AppService, UserService } from '../../services'
import { AppState } from '../../stores/app'
import { Container } from 'typescript-ioc/es5'
import { Button, Drawer, Well } from '@pushly/aqe/lib/components'
import { UserDto } from '../../dtos/user'
import { getClassNames } from '../../_utils/classnames'
import { EditOutlined } from '@ant-design/icons'
import randomstring from 'randomstring'
import { simpleFormErrorNotification, simpleNotification } from '../../_utils/utils'
import { PasswordForm } from '../../components/password-form/password-form'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { Form, Input, Skeleton } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import { useForm } from 'antd/es/form/Form' // Import compatibility layer

interface IUserCredsEditor {
    user: UserDto
    visible: boolean
    onClose: (e: any) => void
    onUpdated: (user: Partial<UserDto>) => void
}

export const UserCredentialsEditor: FC<IUserCredsEditor> = ({ user, visible, onClose, onUpdated }) => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    const userService = useService(UserService)

    const [isDirty, setIsDirty] = useState(false)
    const [form] = useForm()

    const platformUser = appState.currentUser
    const userIsPlatformUser = platformUser?.id === user.id

    const getCurrentValues = (formValues: any) => {
        const values: Partial<UserDto & { currentPassword: string; password: string }> = {}

        const pwd = formValues.password
        const pwdVerify = formValues.passwordVerify

        if (pwd && pwdVerify && pwdVerify === pwd) {
            values.password = pwd.trim()
        }

        if (formValues.currentPassword) {
            values.currentPassword = formValues.currentPassword.trim()
        }

        return values
    }

    const handleSubmit = async () => {
        try {
            const formValues = await form.validateFields()
            const values = getCurrentValues(formValues) as any

            values.confirmationRequired = true

            const { ok, data } = await userService.updateById(user.id, values, {
                showLoadingScreen: true,
            })

            if (ok) {
                onUpdated(data!)
                onClose(null)
                simpleNotification('success', `User, ${user.name}, has been successfully updated.`)
            }
        } catch (err) {
            if (err.errorFields) return // Validation failed, antd handles showing errors
            simpleFormErrorNotification(err)
        }
    }

    const handleValuesChange = () => {
        const currentFields = form.getFieldsValue(true)
        const changedValues = getCurrentValues(currentFields)
        setIsDirty(Object.keys(changedValues).length > 0)
    }

    return (
        <Drawer
            getContainer={appService.getAppContainer}
            className={getClassNames('user-details-editor')}
            title="Edit Credentials"
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            disableSubmit={!isDirty}
            onClose={onClose}
            open={visible}
            width={800}
        >
            {userIsPlatformUser ? (
                <Form
                    {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                    form={form}
                    onValuesChange={handleValuesChange}
                    labelAlign="left"
                    colon={false}
                    requiredMark={false}
                    size="small"
                >
                    <Form.Item
                        name="currentPassword"
                        label="Current Password"
                        rules={[
                            {
                                required: true,
                                message: 'You must confirm your current password to make changes to your account.',
                            },
                        ]}
                    >
                        <Input.Password placeholder="Current Password" autoComplete="current-password" />
                    </Form.Item>
                    <PasswordForm user={user} />
                </Form>
            ) : null}
        </Drawer>
    )
}

interface IUserCreds {
    user: UserDto | any
    loading: boolean
    onUpdated: (account: Partial<UserDto>) => void
    emptyUserState?: React.ReactNode
}

interface IUserCredsState {
    showDrawer: boolean
    drawerKey?: string
}

export class UserCredentialsWell extends React.Component<IUserCreds, IUserCredsState> {
    protected appService: AppService
    protected appState: AppState
    protected userService: UserService

    public constructor(props: IUserCreds) {
        super(props)

        this.appService = Container.get(AppService)
        this.appState = Container.get(AppState)
        this.userService = Container.get(UserService)

        this.state = {
            showDrawer: false,
        }
    }

    public render() {
        const user = this.props.user
        const platformUser = this.appState.currentUser
        const userIsPlatformUser = user && platformUser?.id === user.id
        return (
            <Well className="nested" title="Security" hideFooter={true} actions={this.buildActions()}>
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading &&
                        (!this.props.user && this.props.emptyUserState ? (
                            this.props.emptyUserState
                        ) : userIsPlatformUser ? (
                            <>
                                {this.renderCredentialDetails()}

                                <UserCredentialsEditor
                                    key={this.state.drawerKey}
                                    visible={this.state.showDrawer}
                                    user={this.props.user}
                                    onClose={this.handleDrawerClose}
                                    onUpdated={this.props.onUpdated}
                                />
                            </>
                        ) : null)}
                </Skeleton>
            </Well>
        )
    }

    protected renderCredentialDetails = () => {
        const user = this.props.user
        const platformUser = this.appState.currentUser
        const userIsPlatformUser = platformUser?.id === user.id

        return (
            <Well title="Credentials" mode="ghost" hideFooter={true}>
                {userIsPlatformUser && (
                    <div className={getClassNames('form-layout-row')}>
                        <div className={getClassNames('form-layout-row-label')}>Password</div>
                        <div className={getClassNames('form-layout-row-value')}>*****</div>
                    </div>
                )}
            </Well>
        )
    }

    protected buildActions(): React.ReactNode {
        const actions: any[] = []

        actions.push(
            <span key="edit">
                <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                    <EditOutlined />
                    <span>Change Password</span>
                </Button>
            </span>,
        )

        return actions
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }
}
